/**
 * Bundle PDP behavior.
 *
 * Mounts on every [data-bundle-product] root. Tracks per-component variant
 * selection in memory, recomputes the worst-case bundle ETA on every change,
 * keeps the "N/M sized" counter and the CTA in sync, and on submit posts the
 * bundle product to /cart/add.js with line item properties capturing each
 * component's selected variant. Cart drawer refresh follows the existing
 * pattern (cart:build event + .js-drawer-open-cart click).
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.bundleInit === '1') return;
    root.dataset.bundleInit = '1';

    var sectionId = root.dataset.sectionId;
    var bundleVariantId = Number(root.dataset.bundleVariantId);
    var componentCount = Number(root.dataset.componentCount) || 0;
    var bundleSoldOut = root.dataset.bundleSoldOut === 'true';

    var etaDataEl = document.getElementById('BundleEtaData-' + sectionId);
    var etaMap = {};
    if (etaDataEl) {
      try { etaMap = JSON.parse(etaDataEl.textContent); } catch (e) { etaMap = {}; }
    }

    // Selection state, keyed by component product id (string)
    var selections = {};
    var componentEls = root.querySelectorAll('[data-bundle-component]');

    componentEls.forEach(function (el) {
      var id = el.dataset.componentId;
      var defaultId = el.dataset.defaultVariant;
      var pickerName = el.dataset.pickerName || '';
      var requiredOptions = Number(el.dataset.requiredOptions) || 0;

      var variants = [];
      var variantsScript = el.querySelector('script[data-bundle-variants]');
      if (variantsScript) {
        try { variants = JSON.parse(variantsScript.textContent); } catch (e) { variants = []; }
      }

      if (defaultId) {
        selections[id] = {
          variantId: Number(defaultId),
          title: el.dataset.componentTitle,
          variantTitle: '',
          needsSelection: false,
          pickerName: pickerName,
          requiredOptions: 0,
          optionValues: {},
          variants: variants
        };
      } else {
        selections[id] = {
          variantId: null,
          title: el.dataset.componentTitle,
          variantTitle: '',
          needsSelection: true,
          pickerName: pickerName,
          requiredOptions: requiredOptions,
          optionValues: {},
          variants: variants
        };
      }
      bindComponentOptions(el, id);
      updateAvailability(el, selections[id]);
    });

    bindGallery();
    bindAddToCart();
    bindStickyCtaShadow();
    bindKiwiSizeCharts();
    refresh();

    function bindKiwiSizeCharts() {
      // The Kiwi app block loaded by the section exposes window.ks.loadSizing
      // and window.loadIndividualKiwiSizingChart once SizingPlugin.prod.js
      // finishes loading. We poll briefly because that script is loaded
      // async by Kiwi's own snippet.
      var targets = root.querySelectorAll('[data-bundle-kiwi-target]');
      if (!targets.length) return;
      var pending = [];
      targets.forEach(function (targetEl) {
        var card = targetEl.closest('[data-bundle-component]');
        if (!card) return;
        var dataEl = card.querySelector('script[data-bundle-kiwi-product]');
        if (!dataEl) return;
        var productData;
        try { productData = JSON.parse(dataEl.textContent); } catch (e) { return; }
        if (!productData || !productData.product) return;
        pending.push({ targetEl: targetEl, productData: productData });
      });
      if (!pending.length) return;

      var attempts = 0;
      var maxAttempts = 60; // ~6s at 100ms cadence
      function tryLoad() {
        if (window.ks && typeof window.ks.loadSizing === 'function') {
          pending.forEach(function (item) {
            try {
              window.ks.loadSizing({
                productData: item.productData,
                options: { el: item.targetEl, fallbackToBlockPosition: false }
              });
            } catch (e) { /* swallow — Kiwi just won't render for that one */ }
          });
          return;
        }
        if (++attempts < maxAttempts) {
          setTimeout(tryLoad, 100);
        }
      }
      tryLoad();
    }

    function bindComponentOptions(componentEl, componentId) {
      var optionButtons = componentEl.querySelectorAll('[data-bundle-option]');
      optionButtons.forEach(function (btn) {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        btn.addEventListener('click', function () {
          var sel = selections[componentId];
          var optionIndex = Number(btn.dataset.optionIndex);
          var optionValue = btn.dataset.optionValue || '';

          // Update per-option selection
          sel.optionValues[optionIndex] = optionValue;

          // Visual: clear within THIS picker only (other pickers retain their selection)
          var picker = btn.closest('[data-bundle-picker]');
          var scope = picker || componentEl;
          scope.querySelectorAll('[data-bundle-option]').forEach(function (b) {
            b.classList.remove('is-selected');
            b.setAttribute('aria-checked', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-checked', 'true');

          // Update this picker's value display
          if (picker) {
            var valueEl = picker.querySelector('[data-bundle-component-value]');
            if (valueEl) valueEl.textContent = '· ' + optionValue;
          }

          // Resolve to a variant if all required options are chosen
          var resolved = resolveVariant(sel);
          if (resolved) {
            sel.variantId = resolved.id;
            sel.variantTitle = buildVariantTitle(sel);
          } else {
            sel.variantId = null;
            sel.variantTitle = '';
          }

          // Update status pill (set state only when variant fully resolved)
          var statusEl = componentEl.querySelector('[data-bundle-component-status]');
          if (statusEl) {
            var label = statusEl.querySelector('.bundle-component-card__status-label');
            var dot = statusEl.querySelector('.bundle-component-card__status-dot');
            if (resolved) {
              statusEl.classList.add('bundle-component-card__status-pill--set');
              if (label) label.textContent = sel.variantTitle;
              if (dot) {
                dot.classList.remove('bundle-component-card__status-dot--unset');
                dot.classList.add('bundle-component-card__status-dot--set');
              }
            } else {
              statusEl.classList.remove('bundle-component-card__status-pill--set');
              if (dot) {
                dot.classList.remove('bundle-component-card__status-dot--set');
                dot.classList.add('bundle-component-card__status-dot--unset');
              }
              if (label) label.textContent = sel.requiredOptions > 1 ? 'Choose options' : 'Choose ' + (sel.pickerName || 'option');
            }
          }

          // Per-component ETA reflects the resolved variant when available
          if (resolved) {
            var etaEl = componentEl.querySelector('[data-bundle-component-eta]');
            if (etaEl) {
              var entry = etaMap[componentId] && etaMap[componentId][resolved.id];
              if (entry && entry.message) etaEl.textContent = entry.message;
            }
          }

          // Only swap the thumbnail when the customer changes the color
          // (size/length/etc. shouldn't change the photo — it's visually
          // jarring and the photo doesn't actually reflect the size choice).
          var pickerOptName = picker && (picker.dataset.optionName || '').toLowerCase();
          if (pickerOptName === 'color' || pickerOptName === 'colour') {
            updateThumb(componentEl, resolved);
          }
          updateAvailability(componentEl, sel);
          refresh();
        });
      });
    }

    /**
     * Walks the component's variant matrix and returns the available variant
     * whose options match every selected option-value the customer has
     * chosen so far. Returns null until all required options are picked, or
     * when the chosen combination has no available variant — that null is
     * what keeps the CTA disabled instead of letting the customer add a
     * sold-out item to cart.
     */
    function resolveVariant(sel) {
      if (!sel.variants || !sel.variants.length) return null;
      var chosenIndexes = Object.keys(sel.optionValues);
      if (chosenIndexes.length < sel.requiredOptions) return null;

      for (var i = 0; i < sel.variants.length; i++) {
        var v = sel.variants[i];
        if (!v.available) continue;
        var match = true;
        for (var j = 0; j < chosenIndexes.length; j++) {
          var idx = Number(chosenIndexes[j]);
          if (v.options[idx] !== sel.optionValues[chosenIndexes[j]]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    /**
     * After every selection change, re-evaluate which buttons in this card
     * are currently reachable. A button is "context-unavailable" when no
     * available variant exists for the combination of (everything else
     * selected) + (this button's value). Server-side rendering already
     * disables values that have NO available variant in any combination
     * (aria-disabled); this layer adds dynamic graying for the per-combo
     * case the user described — e.g. size 5 is in stock in Red but sold
     * out in Pink, so it should gray out the moment Pink is selected.
     */
    function updateAvailability(componentEl, sel) {
      if (!sel.variants || !sel.variants.length) return;
      var pickers = componentEl.querySelectorAll('[data-bundle-picker]');
      pickers.forEach(function (picker) {
        var pickerIdx = Number(picker.dataset.optionIndex);
        picker.querySelectorAll('[data-bundle-option]').forEach(function (btn) {
          // Don't override values the server has already marked globally unavailable
          if (btn.classList.contains('bundle-component-card__option--unavailable')) return;
          var value = btn.dataset.optionValue;
          var avail = false;
          for (var i = 0; i < sel.variants.length; i++) {
            var v = sel.variants[i];
            if (!v.available) continue;
            if (v.options[pickerIdx] !== value) continue;
            var match = true;
            Object.keys(sel.optionValues).forEach(function (k) {
              var idx = Number(k);
              if (idx === pickerIdx) return;
              if (v.options[idx] !== sel.optionValues[k]) match = false;
            });
            if (match) { avail = true; break; }
          }
          btn.classList.toggle('is-context-unavailable', !avail);
        });
      });
    }

    /**
     * Swap the component card thumbnail to the resolved variant's image, if
     * the variant has its own assigned image. Falls back to the product's
     * featured image (i.e. the original server-rendered thumbnail) when no
     * variant is resolved or the resolved variant has no image of its own.
     */
    function updateThumb(componentEl, resolved) {
      var thumbImg = componentEl.querySelector('.bundle-component-card__thumb img');
      if (!thumbImg) return;
      if (!thumbImg.dataset.originalSrc) {
        thumbImg.dataset.originalSrc = thumbImg.getAttribute('src') || '';
        thumbImg.dataset.originalSrcset = thumbImg.getAttribute('srcset') || '';
      }
      if (resolved && resolved.image) {
        thumbImg.removeAttribute('srcset');
        thumbImg.setAttribute('src', resolved.image);
      } else {
        if (thumbImg.dataset.originalSrc) {
          thumbImg.setAttribute('src', thumbImg.dataset.originalSrc);
        }
        if (thumbImg.dataset.originalSrcset) {
          thumbImg.setAttribute('srcset', thumbImg.dataset.originalSrcset);
        }
      }
    }

    function buildVariantTitle(sel) {
      var parts = [];
      Object.keys(sel.optionValues)
        .sort(function (a, b) { return Number(a) - Number(b); })
        .forEach(function (k) { parts.push(sel.optionValues[k]); });
      return parts.join(' / ');
    }

    /**
     * Toggles a shadow on the sticky CTA bar only when it's actually pinned
     * to the viewport bottom. The sentinel sits in normal flow just after
     * the bar; while the user is scrolling through the buying area the
     * sentinel is below the viewport (bar is stuck → shadow on), and once
     * the user reaches the bar's resting position the sentinel comes into
     * view (bar is released → shadow off). When the user scrolls far past
     * the buying area the sentinel goes above the viewport — no shadow,
     * but it doesn't matter visually since the bar is off-screen too.
     */
    function bindStickyCtaShadow() {
      var ctaBar = root.querySelector('.bundle-product__cta-bar');
      var sentinel = root.querySelector('[data-bundle-cta-sentinel]');
      if (!ctaBar || !sentinel || typeof IntersectionObserver !== 'function') return;

      var observer = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        var below = entry.boundingClientRect.top > 0 && !entry.isIntersecting;
        ctaBar.classList.toggle('is-stuck', below);
      }, { threshold: 0 });
      observer.observe(sentinel);
    }

    function bindGallery() {
      // The gallery markup is rendered by snippets/product-images.liquid —
      // same DOM the standard PDP uses — so we just run the standard
      // Impulse slideshow init against it. theme.Slideshow wraps Flickity
      // (swipe, keyboard, drag, thumb sync) and theme.Photoswipe handles
      // the magnifier-icon zoom-to-fullscreen. Both are global theme.*
      // classes already loaded by theme.js.
      var imagesContainer = root.querySelector('[data-product-images]');
      var mainSlider = root.querySelector('[data-product-photos]');
      var thumbSlider = root.querySelector('[data-product-thumbs]');
      if (!mainSlider) return;

      var slides = mainSlider.querySelectorAll('.product-main-slide');
      // Single-image case: skip the slideshow but still wire zoom.
      if (slides.length <= 1) {
        if (slides[0]) slides[0].classList.add('is-selected');
      } else if (window.theme && typeof window.theme.Slideshow === 'function') {
        var thumbScroller = thumbSlider ? thumbSlider.querySelector('.product__thumbs--scroller') : null;
        new window.theme.Slideshow(mainSlider, {
          dragThreshold: 25,
          adaptiveHeight: true,
          avoidReflow: true,
          initialIndex: 0,
          childNav: thumbSlider,
          childNavScroller: thumbScroller,
          childVertical: thumbSlider && thumbSlider.dataset.position === 'beside',
          pageDots: true,
          wrapAround: true
        });
      }

      if (imagesContainer && window.theme && typeof window.theme.Photoswipe === 'function') {
        new window.theme.Photoswipe(imagesContainer, sectionId);
      }
    }

    function refresh() {
      // Walk DOM order so the missing-list matches the user's reading order
      var componentEls = root.querySelectorAll('[data-bundle-component]');
      var sized = 0;
      var missingPickerNames = [];
      componentEls.forEach(function (el) {
        var sel = selections[el.dataset.componentId];
        if (!sel || !sel.needsSelection) return;
        if (sel.variantId) {
          sized += 1;
        } else {
          missingPickerNames.push((sel.pickerName || 'option').toLowerCase());
        }
      });

      // Update counter
      var counterEl = root.querySelector('[data-bundle-sized-count]');
      if (counterEl) counterEl.textContent = String(sized);

      var ctaBtn = root.querySelector('[data-bundle-add-to-cart]');
      var ctaText = root.querySelector('[data-bundle-cta-text]');
      var remaining = missingPickerNames.length;

      if (bundleSoldOut) {
        if (ctaBtn) ctaBtn.setAttribute('aria-disabled', 'true');
        if (ctaText) ctaText.textContent = 'Sold out';
      } else if (remaining > 0) {
        if (ctaBtn) ctaBtn.setAttribute('aria-disabled', 'true');
        if (ctaText) ctaText.textContent = buildMissingText(remaining, missingPickerNames);
      } else {
        if (ctaBtn) ctaBtn.removeAttribute('aria-disabled');
        if (ctaText) ctaText.textContent = 'Add bundle to cart (' + componentCount + ')';
      }

      updateEtaBox();
    }

    /**
     * Builds the disabled-CTA prompt. If every missing component shares the
     * same option name (e.g. all "size", or all "color"), use that word so
     * the customer reads exactly what's left to do. If they differ, fall
     * back to "selection" so we don't lie about the missing type.
     */
    function buildMissingText(remaining, names) {
      var unique = {};
      names.forEach(function (n) { unique[n] = true; });
      var keys = Object.keys(unique);
      var word;
      if (keys.length === 1 && keys[0]) {
        word = keys[0];
      } else {
        word = 'selection';
      }
      var plural = remaining === 1 ? word : word + 's';
      return 'Choose ' + remaining + ' more ' + plural + ' to continue';
    }

    /**
     * Find the first required component without a selection, scroll it into
     * view, and flash a brief background highlight so the customer can
     * locate it without scanning the whole list. Triggered when the
     * customer clicks the (aria-)disabled CTA.
     */
    function jumpToFirstMissing() {
      var componentEls = root.querySelectorAll('[data-bundle-component]');
      for (var i = 0; i < componentEls.length; i++) {
        var sel = selections[componentEls[i].dataset.componentId];
        if (sel && sel.needsSelection && !sel.variantId) {
          var target = componentEls[i];
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target.classList.remove('is-attention');
          // Force reflow so the animation restarts if a previous flash is mid-run
          void target.offsetWidth;
          target.classList.add('is-attention');
          window.setTimeout(function () { target.classList.remove('is-attention'); }, 1700);
          return;
        }
      }
    }

    function updateEtaBox() {
      var detailEl = root.querySelector('[data-bundle-eta-detail]');
      if (!detailEl) return;
      var worst = computeWorstEta();
      // If we couldn't compute a worst-case (no ETA data at all), leave the
      // server-rendered placeholder in place rather than blanking the line.
      if (worst.message) detailEl.textContent = worst.message;
    }

    /**
     * Worst-case ETA across the bundle.
     *
     * For each component:
     *   - if a variant has been resolved, use that variant's ETA
     *   - otherwise, take the slowest variant in that component's ETA map —
     *     since the bundle ships in one box, the worst plausible component
     *     ETA is the right placeholder until the customer picks. This fixes
     *     the case where a preorder component (e.g. "Arrives Early June")
     *     wasn't reflected in the top-of-PDP ETA box until the customer
     *     happened to pick a size.
     *
     * The bundle ETA is the max across components.
     */
    function computeWorstEta() {
      var maxDays = -1;
      var maxMessage = '';
      Object.keys(selections).forEach(function (id) {
        var sel = selections[id];
        var componentMap = etaMap[id];
        if (!componentMap) return;

        var componentDays = -1;
        var componentMessage = '';

        if (sel.variantId && componentMap[sel.variantId]) {
          var entry = componentMap[sel.variantId];
          if (typeof entry.days === 'number') {
            componentDays = entry.days;
            componentMessage = entry.message || '';
          }
        } else {
          Object.keys(componentMap).forEach(function (vid) {
            var e = componentMap[vid];
            if (e && typeof e.days === 'number' && e.days > componentDays) {
              componentDays = e.days;
              componentMessage = e.message || '';
            }
          });
        }

        if (componentDays > maxDays) {
          maxDays = componentDays;
          maxMessage = componentMessage;
        }
      });
      return { days: maxDays, message: maxMessage };
    }

    function bindAddToCart() {
      var ctaBtn = root.querySelector('[data-bundle-add-to-cart]');
      var errorEl = root.querySelector('[data-bundle-error]');
      if (!ctaBtn) return;

      ctaBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (ctaBtn.getAttribute('aria-disabled') === 'true') {
          jumpToFirstMissing();
          return;
        }

        // Build line item properties
        var properties = {};

        // Worst-case ETA (matches PDP/UpCart/sticky-buy convention).
        // By the time we reach this branch every component has resolved a
        // variant (the CTA is otherwise aria-disabled), so the per-component
        // fallback in computeWorstEta is a no-op here.
        var worst = computeWorstEta();
        if (worst.message) properties.ETA = worst.message;

        // One property per component, customer-facing label
        Object.keys(selections).forEach(function (id) {
          var sel = selections[id];
          if (!sel.variantId) return;
          var label = sel.title || ('Component ' + id);
          properties[label] = sel.variantTitle || 'One size';
        });

        // Private machine-readable list of component variants the Cart
        // Transform Function reads to populate `line_item.item_components`.
        // Leading underscore hides the property from cart/checkout UI.
        var componentLines = [];
        Object.keys(selections).forEach(function (id) {
          var sel = selections[id];
          if (!sel.variantId) return;
          componentLines.push({
            id: 'gid://shopify/ProductVariant/' + sel.variantId,
            quantity: 1
          });
        });
        if (componentLines.length > 0) {
          properties._bundle_components = JSON.stringify(componentLines);
        }

        var formData = {
          items: [{
            id: bundleVariantId,
            quantity: 1,
            properties: properties
          }]
        };

        ctaBtn.disabled = true;
        var ctaText = root.querySelector('[data-bundle-cta-text]');
        var prevText = ctaText ? ctaText.textContent : '';
        if (ctaText) ctaText.textContent = 'Adding…';
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

        fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData)
        })
          .then(function (r) {
            if (!r.ok) return r.json().then(function (err) { throw err; });
            return r.json();
          })
          .then(function () {
            document.dispatchEvent(new Event('cart:build'));
            var cartOpen = document.querySelector('.js-drawer-open-cart');
            if (cartOpen) cartOpen.click();
            ctaBtn.disabled = false;
            if (ctaText) ctaText.textContent = prevText;
          })
          .catch(function (err) {
            ctaBtn.disabled = false;
            if (ctaText) ctaText.textContent = prevText;
            if (errorEl) {
              errorEl.hidden = false;
              errorEl.textContent = (err && err.description) || (err && err.message) || 'Could not add bundle to cart. Please try again.';
            }
            console.error('[bundle] add to cart failed', err);
          });
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-bundle-product]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-bundle-product]');
    if (root) init(root);
  });
})();
